import React, { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import {
  GetAllModifierItemsByModifierId,
  ChangeIsOptionalStatusOfItemModifier,
  ChangeIsPriceSetterStatusOfItemModifier,
} from "../../../../../../../../crud/menu_api/selection_modifiers";
import { toast } from "react-toastify";
import RemoveItemModifier from "./components/RemoveItemModifier/RemoveItemModifier";
import CreateItemModifier from "./components/CreateItemModifier/CreateItemModifier";

interface Modifier {
  modifier_id: number;
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
}

interface ItemModifier {
  item_id: number;
  item_name: string;
  modifier_id: number;
  is_optional: number; // 1 for true, 0 for false
  is_price_setter: number; // 1 for true, 0 for false
}

interface TableProps {
  mod: Modifier;
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
}

const ItemsTable: React.FC<TableProps> = ({ mod, showCreateModal, setShowCreateModal }) => {
  const [items, setItems] = useState<ItemModifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemModifier | null>(null);

  useEffect(() => {
    setLoading(true);
    GetAllModifierItemsByModifierId(mod.modifier_id)
      .then((response) => {
        setItems(response.data);
      })
      .catch((error) => {
        toast.error("Error fetching modifier items");
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once

  const handleToggleOptional = (item: ItemModifier) => {
    ChangeIsOptionalStatusOfItemModifier(item.item_id, item.modifier_id)
      .then(() => {
        // Toggle the is_optional value locally
        setItems((prev) =>
          prev.map((i) =>
            i.item_id === item.item_id && i.modifier_id === item.modifier_id
              ? { ...i, is_optional: i.is_optional ? 0 : 1 }
              : i
          )
        );
        toast.success("Estado opcional actualizado");
      })
      .catch((error) => {
        console.error("Error al cambiar estado opcional", error);
        toast.error("Error al cambiar estado opcional");
      });
  };

  const handleTogglePriceSetter = (item: ItemModifier) => {
    ChangeIsPriceSetterStatusOfItemModifier(item.item_id, item.modifier_id)
      .then(() => {
        // Toggle the is_price_setter value locally
        setItems((prev) =>
          prev.map((i) =>
            i.item_id === item.item_id && i.modifier_id === item.modifier_id
              ? { ...i, is_price_setter: i.is_price_setter ? 0 : 1 }
              : i
          )
        );
        toast.success("Estado fijador de precio actualizado");
      })
      .catch((error) => {
        console.error("Error al cambiar estado fijador de precio", error);
        toast.error("Error al cambiar estado fijador de precio");
      });
  };

  const handleRemoveItemFromGroup = (item: ItemModifier) => {
    setShowDeleteModal(true);
    setSelectedItem(item);
  };

  // Render content based on state
  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <div className="loading-container">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <p>Cargando productos vinculados...</p>
        </div>
      );
    }

    // Empty state
    if (items.length === 0) {
      return (
        <div className="items-empty-state">
          <p>No hay productos vinculados a este modificador.</p>
        </div>
      );
    }

    // Table with items
    return (
      <table className="sg-items-table plain">
        <thead>
          <tr>
            <th className="col-name">Nombre</th>
            <th>Opcional</th>
            <th>Fijador de precio</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item: ItemModifier, index: number) => (
            <tr key={item.item_id}>
              <td className="col-name">{item.item_name}</td>
              <td>
                <button
                  className={`toggle-btn ${item.is_optional ? "active" : ""}`}
                  onClick={() => handleToggleOptional(item)}
                  aria-label={`Cambiar estado opcional para ${item.item_name}`}
                >
                  {item.is_optional ? "Si" : "No"}
                </button>
              </td>
              <td>
                <button
                  className={`toggle-btn ${item.is_price_setter ? "active" : ""}`}
                  onClick={() => handleTogglePriceSetter(item)}
                  aria-label={`Cambiar estado fijador de precio para ${item.item_name}`}
                >
                  {item.is_price_setter ? "Si" : "No"}
                </button>
              </td>
              <td className="cell-actions">
                <button className="icon-btn danger" onClick={() => handleRemoveItemFromGroup(item)} aria-label="Eliminar ítem del modificador">
                  <FaTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      {renderContent()}

      {showCreateModal && (
        <CreateItemModifier
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          mod={mod}
          setItems={setItems}
          items={items}
        />
      )}

      {showDeleteModal && mod && selectedItem && (
        <RemoveItemModifier
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          mod={mod}
          item={selectedItem}
          setItems={setItems}
          items={items}
        />
      )}
    </>
  );
};

export default ItemsTable;
